import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, FileText, Download } from "lucide-react";
import { getRapports, getActivites } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const anneesActivite = (date) => {
  const trouvees = (date.match(/\b20\d{2}\b/g) || []).map(Number);
  if (date.includes(" à ") && trouvees.length >= 2) {
    const [debut, fin] = [Math.min(...trouvees), Math.max(...trouvees)];
    return Array.from({ length: fin - debut + 1 }, (_, i) => debut + i);
  }
  return trouvees;
};

export default function Impact() {
  const [rapports, setRapports] = useState([]);
  const [activites, setActivites] = useState([]);
  const [anneeFiltre, setAnneeFiltre] = useState("Toutes");
  const [chargement, setChargement] = useState(true);

  const annees = [...new Set(activites.flatMap((a) => anneesActivite(a.date)))].sort((a, b) => a - b);
  const activitesVisibles = activites
    .filter((a) => anneeFiltre === "Toutes" || anneesActivite(a.date).includes(Number(anneeFiltre)))
    .sort((a, b) => Math.min(...anneesActivite(a.date)) - Math.min(...anneesActivite(b.date)));

  useEffect(() => {
    Promise.all([getRapports(), getActivites()])
      .then(([r, a]) => {
        setRapports(r);
        setActivites(a);
      })
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  return (
    <>
      <section className="bg-espresso py-28 text-ivory" data-testid="impact-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Notre impact</p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="impact-title">
              La transparence, une question de <span className="italic text-primary">confiance</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/70 md:text-lg">
              Rapports annuels, activités passées et résultats : nous rendons compte de chaque
              action et de chaque dollar confié à la Fondation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-20 md:py-28" data-testid="rapports-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Rapports annuels</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Nos années en revue
            </h2>
          </Reveal>

          {chargement ? (
            <p className="mt-16 text-foreground/50" data-testid="impact-loading">Chargement…</p>
          ) : (
            <div className="mt-14 space-y-0" data-testid="rapports-list">
              {rapports.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.08}>
                  <div className="grid gap-8 border-t border-border py-12 lg:grid-cols-12" data-testid={`rapport-${r.annee}`}>
                    <div className="lg:col-span-3">
                      <span className="font-serif text-6xl font-medium text-gold md:text-7xl" data-testid={`rapport-annee-${r.annee}`}>{r.annee}</span>
                    </div>
                    <div className="lg:col-span-6">
                      <h3 className="flex items-start gap-3 font-serif text-2xl font-semibold leading-snug md:text-3xl">
                        <FileText size={24} strokeWidth={1.5} className="mt-1.5 shrink-0 text-primary" />
                        {r.titre}
                      </h3>
                      <ul className="mt-6 space-y-3">
                        {r.points_saillants.map((p, j) => (
                          <li key={j} className="flex items-start gap-3 text-sm leading-relaxed text-foreground/70 md:text-base">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex items-start lg:col-span-3 lg:justify-end">
                      {r.pdf_path ? (
                        <a
                          href={`${BACKEND}/api/rapports/${r.id}/pdf`}
                          target="_blank"
                          rel="noopener"
                          data-testid={`rapport-pdf-${r.annee}`}
                          className="group flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
                        >
                          <Download size={16} className="transition-transform duration-200 group-hover:translate-y-0.5" />
                          Télécharger le PDF
                        </a>
                      ) : (
                        <Link
                          to="/contact"
                          data-testid={`rapport-demande-${r.annee}`}
                          className="group flex items-center gap-2 rounded-sm border border-foreground/20 px-6 py-3 text-sm font-semibold transition-colors duration-200 hover:border-primary hover:text-primary"
                        >
                          Recevoir le rapport
                          <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-border" />
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-border bg-card py-20 md:py-28" data-testid="activites-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Activités antérieures</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Une frise qui grandit avec la communauté
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <div className="border border-border bg-background p-8 lg:sticky lg:top-36" data-testid="impact-total-card">
                <p className="font-serif text-7xl font-medium leading-none text-gold" data-testid="impact-total-events">27</p>
                <p className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-primary">événements depuis 2021</p>
                <p className="mt-5 text-sm leading-relaxed text-foreground/60">
                  Explorez les jalons publics par année. De nouvelles activités peuvent être ajoutées à cette frise depuis l'espace admin.
                </p>
              </div>
            </Reveal>

            <div className="lg:col-span-8">
              <div className="flex flex-wrap gap-3" data-testid="impact-year-filters">
                {["Toutes", ...annees].map((annee) => (
                  <button
                    key={annee}
                    onClick={() => setAnneeFiltre(String(annee))}
                    data-testid={`impact-year-filter-${String(annee).toLowerCase()}`}
                    className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
                      anneeFiltre === String(annee)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-foreground/65 hover:border-primary hover:text-primary"
                    }`}
                  >
                    {annee}
                  </button>
                ))}
              </div>

              <div className="relative mt-12 space-y-10 before:absolute before:bottom-4 before:left-[11px] before:top-2 before:w-px before:bg-border" data-testid="activites-timeline">
                {activitesVisibles.map((a, i) => (
                  <Reveal key={a.id} delay={i * 0.06}>
                    <article className="relative pl-12" data-testid={`activite-timeline-${i}`}>
                      <span className="absolute left-0 top-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-gold bg-card">
                        <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
                      </span>
                      <div className="border border-border bg-background p-7 transition-[border-color,transform] duration-200 hover:-translate-y-1 hover:border-primary/50">
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold uppercase tracking-[0.16em] text-foreground/50">
                          <p className="flex items-center gap-2"><Calendar size={14} className="text-primary" /> {a.date}</p>
                          <p className="flex items-center gap-2"><MapPin size={14} className="text-primary" /> {a.lieu}</p>
                        </div>
                        <h3 className="mt-4 font-serif text-2xl font-semibold leading-snug">{a.titre}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-foreground/60 md:text-base">{a.description}</p>
                        {a.photos?.length > 0 && (
                          <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid={`activite-photos-${i}`}>
                            {a.photos.map((photo, j) => (
                              <img
                                key={photo}
                                src={`${BACKEND}/api/media/${photo}`}
                                alt={`${a.titre} — photo ${j + 1}`}
                                loading="lazy"
                                className={`aspect-[4/3] w-full border border-border object-cover ${j === 0 && a.photos.length > 1 ? "sm:col-span-2" : ""}`}
                                data-testid={`activite-photo-${i}-${j}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </article>
                  </Reveal>
                ))}
                {activitesVisibles.length === 0 && (
                  <p className="pl-12 text-sm text-foreground/50" data-testid="impact-timeline-empty">Aucune activité publiée pour cette année.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
